import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/homeData";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isDark = !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-gradient-to-b from-black/40 to-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <span className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300 ${
              isDark ? "bg-background-50/25" : "bg-primary-500"
            }`}>
              <i className={`text-sm transition-colors duration-300 ${
                isDark ? "text-background-50" : "text-background-50"
              } ri-file-list-3-line`}></i>
            </span>
            <span className={`font-heading font-semibold text-lg whitespace-nowrap transition-colors duration-300 ${
              isDark ? "text-background-50" : "text-foreground-950"
            }`}>
              Mulebuy Sheets
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isDark ? "text-background-50/90 hover:text-background-50" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              Home
            </Link>

            {/* Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isDark
                    ? "bg-background-50/20 text-background-50 hover:bg-background-50/30"
                    : "bg-background-100 text-foreground-800 hover:bg-background-200"
                }`}
              >
                <i className="ri-grid-line text-sm"></i>
                Browse Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-80 overflow-y-auto shadow-sm">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-900 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                        <i className={`${cat.icon} text-accent-600 text-xs`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/mulebuy-spreadsheet-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isDark ? "text-background-50/85 hover:text-background-50" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                isDark
                  ? "bg-background-50 text-foreground-900 hover:bg-background-100"
                  : "bg-primary-500 text-background-50 hover:bg-primary-600"
              }`}
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg transition-colors ${
              isDark ? "text-background-50 hover:bg-background-50/10" : "text-foreground-800 hover:bg-background-100"
            }`}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-4-line text-accent-500 w-5 h-5 flex items-center justify-center"></i>
              Home
            </Link>

            {/* Mobile category expander */}
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-accent-500 w-5 h-5 flex items-center justify-center"></i>
                Browse Categories
              </span>
              <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-900 transition-colors cursor-pointer"
                    onClick={() => setMobileOpen(false)}
                  >
                    <span className="w-6 h-6 rounded-md bg-accent-100 flex items-center justify-center shrink-0">
                      <i className={`${cat.icon} text-accent-600 text-xs`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <Link
              to="/mulebuy-spreadsheet-guide"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-book-open-line text-accent-500 w-5 h-5 flex items-center justify-center"></i>
              Shopping Guide
            </Link>

            <div className="pt-2 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-shopping-cart-line"></i>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}